/**
 * 예산 마법사 — 질의 항목을 기존 예산 줄(관·항·목, 수가, 급여 연동)로 바꿉니다.
 */
import { defaultSugaTimesByBizGubun } from "@/utils/budgetBizDefaults";

export type BizFamily = "care" | "bath" | "day" | "facility";

export interface WizardSuga {
  suga_rank: string;
  suga_money: number;
  suga_bonin: number;
}

export interface WizardTax {
  tr_yeon?: number;
  tr_gun?: number;
  tr_jang?: number;
  tr_go?: number;
  tr_sil?: number;
  tr_san?: number;
  tr_labor_cost_rate?: number;
}

export type MoneyMode = "annual" | "monthly" | "span" | "times" | "people";

export interface MoneyField {
  id: string;
  step: string;
  label: string;
  hint: string;
  scode: string;
  trcode: "IN" | "OUT";
  mode: MoneyMode;
  families: BizFamily[];
  /** 계정 기본 자금원천과 다를 때만. 예: 전년도 보조금 잔액은 07 */
  funding?: string;
}

export interface GradeAnswer {
  id: string;
  label: string;
  count: string;
  basicCount: string;
  rank: string;
  times: string;
  /** 주야간: 행마다 고르는 이용 시간. 수가 구간 `(8시간)1등급` 의 앞부분 */
  hours: string;
}

export interface StaffAnswer {
  id: string;
  role: string;
  kind: "direct" | "indirect";
  isCeo: boolean;
  count: string;
  monthly: string;
}

export interface WizardAnswers {
  dayHours: string;
  grades: GradeAnswer[];
  staff: StaffAnswer[];
  treatmentCount: string;
  treatmentSubsidy: string;
  treatmentCopay: string;
  money: Record<string, { a: string; b: string }>;
}

export interface WizardLine {
  trcode: "IN" | "OUT";
  scode: string;
  description: string;
  reviewLabel: string;
  unitPrice: number;
  count: number;
  months: number;
  sugaRank?: string;
  sugaTimes?: string;
  payRel?: string;
  revenueRel?: string;
  isSalary?: boolean;
  isLinked?: boolean;
  /** 예산표 합계(사회보험·퇴직은 인원 합산 후 반올림)와 맞춘 금액 */
  settledAmount?: number;
  /** 계정 기본값과 다른 자금원천 */
  funding?: string;
}

export interface WizardBuildResult {
  lines: WizardLine[];
  income: number;
  expense: number;
  /** 더 이상 차액으로 줄을 만들지 않는다. 항상 0 */
  plug: number;
  /** 더 이상 차액으로 줄을 만들지 않는다. 항상 0 */
  shortfall: number;
}

export interface WizardStep {
  id: string;
  /** 진행 막대에 쓰는 짧은 이름 */
  nav: string;
  title: string;
}

export function budgetWizardFamily(bizGubun: string): BizFamily {
  const b = (bizGubun || "").trim();
  if (b === "방문목욕") return "bath";
  if (b === "주야간보호") return "day";
  if (b === "요양시설" || b === "공동생활가정") return "facility";
  return "care";
}

export function familyUsesBasicSplit(family: BizFamily): boolean {
  return family === "day" || family === "facility";
}

export function familyUsesTreatment(family: BizFamily): boolean {
  return family === "day" || family === "facility";
}

const DIRECT_ROLES = ["요양보호사", "사회복지사"];
const DIRECT_ROLES_CARE_HOME = ["요양보호사", "사회복지사", "간호사", "간호조무사", "물리치료사", "작업치료사"];
const INDIRECT_ROLES = ["센터장", "사회복지사", "사무국장", "사무원", "영양사", "조리원", "조리사", "위생원", "관리인", "보조원 운전사", "기타"];

/** 질의서의 직책선택. 직책 이름은 fnasolution 예산표 목록을 참고했다. */
export function salaryRoleOptions(kind: "direct" | "indirect", bizGubun: string): string[] {
  if (kind === "indirect") return INDIRECT_ROLES;
  const family = budgetWizardFamily(bizGubun);
  if (family === "day" || family === "facility") return DIRECT_ROLES_CARE_HOME;
  return DIRECT_ROLES;
}

const DAY_GRADES = ["1등급", "2등급", "3등급", "4등급", "5등급", "인지지원등급"];

export function wizardSteps(_family: BizFamily): WizardStep[] {
  return [
    { id: "people", nav: "수가 수입", title: "수가 수입" },
    { id: "income", nav: "기타 수입", title: "기타 수입" },
    { id: "staff", nav: "인건비", title: "인건비" },
    { id: "spend", nav: "기타 지출", title: "기타 지출" },
    { id: "review", nav: "확인", title: "확인" },
  ];
}

export const MONEY_FIELDS: MoneyField[] = [
  { id: "addon", step: "benefit", label: "요양급여 가산금", hint: "한 달 기준입니다. 인건비 비율에 포함되는 가산금으로 넣습니다.", scode: "613", trcode: "IN", mode: "monthly", families: ["care", "bath", "day", "facility"] },
  { id: "longService", step: "benefit", label: "장기근속수당", hint: "한 달 기준. 인건비 비율에는 넣지 않는 급여수입으로 둡니다.", scode: "612", trcode: "IN", mode: "monthly", families: ["care", "bath", "day", "facility"] },
  { id: "edu", step: "benefit", label: "보수교육비", hint: "한 달 기준입니다.", scode: "612", trcode: "IN", mode: "monthly", families: ["care", "bath"] },
  { id: "seniorPay", step: "benefit", label: "선임수당", hint: "한 달 기준입니다.", scode: "612", trcode: "IN", mode: "monthly", families: ["day", "facility"] },

  { id: "flu", step: "subsidy", label: "독감예방접종 보조금", hint: "독감예방접종 보조금이 있으면 적습니다.", scode: "413", trcode: "IN", mode: "annual", families: ["care", "bath"] },
  { id: "cityAid", step: "subsidy", label: "시군구보조금", hint: "연간으로 받을 금액입니다.", scode: "413", trcode: "IN", mode: "annual", families: ["day"] },
  { id: "livelihoodIn", step: "subsidy", label: "기초수급자 생계비보조금", hint: "시군구보조금으로 들어오는 연간 금액입니다.", scode: "413", trcode: "IN", mode: "annual", families: ["facility"] },
  { id: "holidayIn", step: "subsidy", label: "명절위로금", hint: "수급자가 시군구에서 직접 받으면 비워 두세요.", scode: "413", trcode: "IN", mode: "annual", families: ["facility"] },
  { id: "specialIn", step: "subsidy", label: "특별위로금", hint: "기관으로 들어오는 연간 금액입니다.", scode: "413", trcode: "IN", mode: "annual", families: ["facility"] },
  { id: "etcSubsidy", step: "subsidy", label: "기타보조금", hint: "복지수당 등 기타 보조금이 있으면 연간 금액입니다.", scode: "414", trcode: "IN", mode: "annual", families: ["care", "bath"] },
  { id: "etcSubsidyHome", step: "subsidy", label: "기타보조금", hint: "복지수당, 처우개선비 등 연간 금액입니다.", scode: "414", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "mealIn", step: "subsidy", label: "식재료비 수입", hint: "간식비가 있으면 포함합니다. 한 달 금액입니다.", scode: "113", trcode: "IN", mode: "monthly", families: ["day", "facility"] },
  { id: "roomIn", step: "subsidy", label: "상급침실료", hint: "연간 금액입니다.", scode: "114", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "beautyIn", step: "subsidy", label: "이미용비", hint: "연간 금액입니다.", scode: "115", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "staffMealIn", step: "subsidy", label: "직원식재료비 수입", hint: "직원에게 받거나 급여에서 공제하는 금액입니다. 한 달 기준입니다.", scode: "1013", trcode: "IN", mode: "monthly", families: ["day", "facility"] },
  { id: "borrow", step: "subsidy", label: "기타차입금", hint: "한 달 기준입니다.", scode: "712", trcode: "IN", mode: "monthly", families: ["care", "bath", "day", "facility"] },
  { id: "transferIn", step: "subsidy", label: "기타전입금", hint: "한 달 기준입니다.", scode: "813", trcode: "IN", mode: "monthly", families: ["care", "bath", "day", "facility"] },

  { id: "carry", step: "carry", label: "전년도이월금", hint: "올해로 넘기는 운영비 잔액입니다.", scode: "911", trcode: "IN", mode: "annual", families: ["care", "bath"] },
  { id: "carryAid", step: "carry", label: "전년도이월금 (보조금)", hint: "전년도 보조금 잔액을 합친 금액입니다. 전년도이월금 목에 보조금으로 들어갑니다.", scode: "911", trcode: "IN", mode: "annual", families: ["day", "facility"], funding: "07" },
  { id: "carryDonate", step: "carry", label: "전년도이월금 (후원금)", hint: "전년도 후원금 잔액입니다.", scode: "912", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "carryMeal", step: "carry", label: "전년도이월금 (식재료비)", hint: "전년도 생계비 잔액입니다.", scode: "913", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "carryOp", step: "carry", label: "전년도이월금 (운영비)", hint: "보조금·후원금·식재료비를 뺀 운영비 잔액입니다.", scode: "911", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "interest", step: "carry", label: "예금이자", hint: "연간으로 예상되는 이자입니다.", scode: "1012", trcode: "IN", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "scrap", step: "carry", label: "불용품 매각대", hint: "차량 등 매각대금이 있으면 연간 금액입니다.", scode: "1011", trcode: "IN", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "miscIn", step: "carry", label: "잡수입", hint: "연말정산 환급, 기타지원금처럼 위에 없는 수입의 연간 금액입니다.", scode: "1014", trcode: "IN", mode: "annual", families: ["care", "bath"] },
  { id: "miscRefund", step: "carry", label: "연말정산 환급금", hint: "기관으로 들어오는 연간 금액입니다.", scode: "1014", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "miscOver", step: "carry", label: "한도초과·등급 외 수입", hint: "기타비급여수입으로 들어갑니다. 연간 금액입니다.", scode: "116", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "miscOther", step: "carry", label: "그 외 수입", hint: "위에 없는 잡수입의 연간 금액입니다.", scode: "1014", trcode: "IN", mode: "annual", families: ["day", "facility"] },
  { id: "reserveUse", step: "carry", label: "운영충당적립금 사용", hint: "이미 적립해 둔 금액 중 올해 수입으로 쓸 돈입니다.", scode: "1111", trcode: "IN", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "improveUse", step: "carry", label: "시설환경개선준비금 사용", hint: "이미 적립해 둔 금액 중 올해 수입으로 쓸 돈입니다.", scode: "1112", trcode: "IN", mode: "annual", families: ["care", "bath", "day", "facility"] },

  { id: "guest", step: "operate", label: "손님 식사·음료", hint: "연간 금액입니다.", scode: "121", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "staffDine", step: "operate", label: "직원 식사·회식", hint: "연간 금액입니다.", scode: "121", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "gift", step: "operate", label: "명절선물", hint: "한 번 금액과 횟수입니다.", scode: "121", trcode: "OUT", mode: "times", families: ["care", "bath", "day", "facility"] },
  { id: "bonus", step: "operate", label: "직원 명절 상여금", hint: "한 번 금액과 횟수입니다. 급여 항목이 아닙니다.", scode: "121", trcode: "OUT", mode: "times", families: ["care", "bath", "day", "facility"] },
  { id: "condolence", step: "operate", label: "경조사비", hint: "직원·어르신 경조사. 한 번 금액과 횟수입니다.", scode: "121", trcode: "OUT", mode: "times", families: ["care", "bath", "day", "facility"] },
  { id: "visit", step: "operate", label: "어르신 방문", hint: "한 번 금액과 횟수입니다.", scode: "121", trcode: "OUT", mode: "times", families: ["care", "bath", "day", "facility"] },
  { id: "consult", step: "operate", label: "기타 업무협의비", hint: "연간 금액입니다.", scode: "121", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "positionPay", step: "operate", label: "직책보조비", hint: "인원과 금액입니다. 금액은 한 달 기준이고 12개월로 잡습니다.", scode: "122", trcode: "OUT", mode: "people", families: ["care", "bath", "day", "facility"] },
  { id: "meeting", step: "operate", label: "회의비", hint: "인원과 금액입니다. 금액은 한 달 기준이고 12개월로 잡습니다.", scode: "123", trcode: "OUT", mode: "people", families: ["care", "bath", "day", "facility"] },
  { id: "travel", step: "operate", label: "여비", hint: "금액을 적습니다.", scode: "131", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },

  { id: "stationery", step: "office", label: "사무용품비", hint: "월 금액입니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "supply", step: "office", label: "소모품비", hint: "월 금액입니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "agency", step: "office", label: "업무대행료", hint: "월 금액입니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "mgmt", step: "office", label: "건물관리비", hint: "월 금액입니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "etcAccept", step: "office", label: "기타수용비", hint: "월 금액입니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "misc", step: "office", label: "기타잡비", hint: "직접 적은 금액만 넣습니다.", scode: "132", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "phone", step: "office", label: "전화·인터넷", hint: "월 금액입니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "elec", step: "office", label: "전기요금", hint: "월 금액입니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "water", step: "office", label: "수도요금", hint: "월 금액입니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "liability", step: "office", label: "배상책임보험", hint: "월로 나누어 적거나, 개월을 1로 두고 연간 보험료를 적어도 됩니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "carIns", step: "office", label: "차량보험", hint: "월 금액입니다. 연간 보험료면 개월을 1로 두세요.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "tax", step: "office", label: "각종세금", hint: "월 금액입니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "etcTax", step: "office", label: "기타공과금", hint: "월 금액입니다.", scode: "133", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },

  { id: "fuel", step: "vehicle", label: "차량주유비", hint: "월 금액입니다.", scode: "134", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "carRepair", step: "vehicle", label: "차량수리비", hint: "월 금액입니다.", scode: "134", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "rent", step: "vehicle", label: "사무실 월 임차료", hint: "한 달 임대료입니다. 12개월로 잡습니다. 없으면 비워 두세요.", scode: "135", trcode: "OUT", mode: "monthly", families: ["care", "bath", "day"] },
  { id: "rentCar", step: "vehicle", label: "차량렌트비", hint: "할부가 아닌 렌트입니다. 월 금액입니다.", scode: "136", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "reward", step: "vehicle", label: "직원포상금", hint: "급여가 아닌 포상입니다. 연간 금액입니다.", scode: "136", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },

  { id: "fixOffice", step: "place", label: "사무실 보수비", hint: "월 금액입니다.", scode: "211", trcode: "OUT", mode: "span", families: ["care", "bath"] },
  { id: "fixBuilding", step: "place", label: "건물 보수비", hint: "월 금액입니다.", scode: "211", trcode: "OUT", mode: "span", families: ["day", "facility"] },
  { id: "equip", step: "place", label: "장비 수리비", hint: "한 달에 10만 원 이상 나갈 때 적습니다. 월 금액입니다.", scode: "213", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "installment", step: "place", label: "차량할부금", hint: "렌트가 아닌 할부입니다. 월 금액입니다.", scode: "212", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "asset", step: "place", label: "비품 구입", hint: "100만 원 이상 비품입니다. 월 금액 또는 개월 1에 총액을 적습니다.", scode: "212", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "living", step: "place", label: "생계비", hint: "어르신과 함께 쓰는 직원 식재료비입니다. 한 달 금액이고 12개월로 잡습니다.", scode: "311", trcode: "OUT", mode: "monthly", families: ["day", "facility"] },
  { id: "livingSub", step: "place", label: "생계비 (보조금)", hint: "기초수급자의 정부보조금입니다. 한 달 금액이고 12개월로 잡습니다.", scode: "312", trcode: "OUT", mode: "monthly", families: ["facility"] },
  { id: "careGoods", step: "place", label: "수용기관경비", hint: "기저귀, 케어용품 등 연간 금액입니다.", scode: "313", trcode: "OUT", mode: "annual", families: ["day", "facility"] },
  { id: "medical", step: "place", label: "의료비", hint: "어르신 진료·약제비의 연간 금액입니다.", scode: "315", trcode: "OUT", mode: "annual", families: ["day", "facility"] },
  { id: "program", step: "place", label: "프로그램비", hint: "연간 금액입니다.", scode: "321", trcode: "OUT", mode: "annual", families: ["day", "facility"] },
  { id: "programMat", step: "place", label: "프로그램 재료비", hint: "연간 금액입니다.", scode: "321", trcode: "OUT", mode: "annual", families: ["day", "facility"] },

  { id: "transferOut", step: "closing", label: "기타전출금", hint: "월 금액입니다.", scode: "411", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "principal", step: "closing", label: "원금상환금", hint: "월 금액입니다.", scode: "611", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "interestOut", step: "closing", label: "이자지불금", hint: "월 금액입니다.", scode: "612", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "refundOut", step: "closing", label: "연말정산 환급 지급", hint: "직원에게 돌려줄 연간 금액입니다.", scode: "711", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "miscOut", step: "closing", label: "기타 잡지출", hint: "위에 없는 지출의 연간 금액입니다.", scode: "711", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "giveBack", step: "closing", label: "반환금", hint: "정부보조금 중 돌려줘야 할 연간 금액입니다.", scode: "812", trcode: "OUT", mode: "annual", families: ["day", "facility"] },
  { id: "reserveSave", step: "closing", label: "운영충당적립금 적립", hint: "올해 새로 쌓을 월 금액입니다.", scode: "911", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "improveSave", step: "closing", label: "시설환경개선준비금 적립", hint: "올해 새로 쌓을 월 금액입니다.", scode: "912", trcode: "OUT", mode: "span", families: ["care", "bath", "day", "facility"] },
  { id: "reserveSpend", step: "closing", label: "운영충당적립금 지출", hint: "통장에 넣어 둔 적립금 중 실제로 쓸 연간 금액입니다.", scode: "1011", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
  { id: "improveSpend", step: "closing", label: "시설환경개선준비금 지출", hint: "통장에 넣어 둔 준비금 중 실제로 쓸 연간 금액입니다.", scode: "1012", trcode: "OUT", mode: "annual", families: ["care", "bath", "day", "facility"] },
];

export function fieldsFor(stepId: string, family: BizFamily): MoneyField[] {
  return MONEY_FIELDS.filter((field) => field.step === stepId && field.families.includes(family));
}

/**
 * 예산표에서 "비어 있을 때 숨길" 목 코드 (사업구분별).
 * 질의서(MONEY_FIELDS)에 해당 사업이 쓰지 않는 전용 목 + 마법사에 없는 구형 목.
 * 금액·내용이 있으면 표에 남긴다.
 */
export function getHiddenScodesWhenEmpty(bizGubun: string): { in: string[]; out: string[] } {
  const family = budgetWizardFamily(bizGubun);

  /** trcode:scode → 이 목을 쓰는 사업군 */
  const usedBy = new Map<string, Set<BizFamily>>();
  const touch = (trcode: "IN" | "OUT", scode: string, fams: BizFamily[]) => {
    const key = `${trcode}:${scode}`;
    if (!usedBy.has(key)) usedBy.set(key, new Set());
    const set = usedBy.get(key)!;
    fams.forEach((f) => set.add(f));
  };

  MONEY_FIELDS.forEach((field) => touch(field.trcode, field.scode, field.families));
  // 처우개선비 — MONEY_FIELDS 밖, 주야간·시설만
  touch("OUT", "1161", ["day", "facility"]);

  const hideIn = new Set<string>();
  const hideOut = new Set<string>();

  usedBy.forEach((fams, key) => {
    if (fams.has(family)) return;
    const [trcode, scode] = key.split(":");
    if (trcode === "IN") hideIn.add(scode);
    else hideOut.add(scode);
  });

  // 질의서에 없는 구형·비사용 목 — 비어 있으면 전 사업 숨김 (기존 HIDDEN_SCODES 잔여)
  ["211"].forEach((s) => hideIn.add(s));
  ["314", "317", "318", "331", "332"].forEach((s) => hideOut.add(s));

  return { in: [...hideIn], out: [...hideOut] };
}

const FIELD_BY_ID = new Map(MONEY_FIELDS.map((field) => [field.id, field]));

interface MoneyMok {
  title: string;
  ids: string[];
}

interface MoneyHang {
  hang: string;
  moks: MoneyMok[];
}

interface MoneyGwan {
  gwan: string;
  hangs: MoneyHang[];
}

export interface MoneySheetGroup {
  title: string;
  fields: MoneyField[];
}

export interface MoneySheetHang {
  hang: string;
  /** 관 이름과 같으면 항 줄을 또 그리지 않는다 */
  showHang: boolean;
  groups: MoneySheetGroup[];
}

export interface MoneySheetGwan {
  gwan: string;
  hangs: MoneySheetHang[];
}

/** 재무코드 관·항·목 순서 */
const SHEET_GWAN: Record<"income" | "spend", MoneyGwan[]> = {
  income: [
    {
      gwan: "입소자(이용자)부담금수입",
      hangs: [{
        hang: "입소(이용)비용수입",
        moks: [
          { title: "식재료비수입", ids: ["mealIn"] },
          { title: "상급침실이용료", ids: ["roomIn"] },
          { title: "이미용비", ids: ["beautyIn"] },
          { title: "기타비급여수입", ids: ["miscOver"] },
        ],
      }],
    },
    {
      gwan: "보조금수입",
      hangs: [{
        hang: "보조금수입",
        moks: [
          { title: "시군구보조금", ids: ["flu", "cityAid", "livelihoodIn", "holidayIn", "specialIn"] },
          { title: "기타보조금", ids: ["etcSubsidy", "etcSubsidyHome"] },
        ],
      }],
    },
    {
      gwan: "요양급여수입",
      hangs: [{
        hang: "요양급여수입",
        moks: [
          { title: "장기요양급여수입", ids: ["longService", "edu", "seniorPay"] },
          { title: "가산금수입", ids: ["addon"] },
        ],
      }],
    },
    {
      gwan: "차입금",
      hangs: [{ hang: "금융기관차입금", moks: [{ title: "기타차입금", ids: ["borrow"] }] }],
    },
    {
      gwan: "전입금",
      hangs: [{ hang: "전입금", moks: [{ title: "기타전입금", ids: ["transferIn"] }] }],
    },
    {
      gwan: "이월금",
      hangs: [{
        hang: "이월금",
        moks: [
          { title: "전년도이월금", ids: ["carry", "carryAid", "carryOp"] },
          { title: "전년도이월금(후원금)", ids: ["carryDonate"] },
          { title: "전년도이월금(식재료비)", ids: ["carryMeal"] },
        ],
      }],
    },
    {
      gwan: "잡수입",
      hangs: [{
        hang: "잡수입",
        moks: [
          { title: "불용품매각대", ids: ["scrap"] },
          { title: "기타예금이자수입", ids: ["interest"] },
          { title: "직원식재료수입", ids: ["staffMealIn"] },
          { title: "기타잡수입", ids: ["miscIn", "miscRefund", "miscOther"] },
        ],
      }],
    },
    {
      gwan: "적립금및준비금(특별회계)",
      hangs: [{
        hang: "운영충당적립금및환경개선준비금",
        moks: [
          { title: "운영충당적립금", ids: ["reserveUse"] },
          { title: "시설환경개선준비금", ids: ["improveUse"] },
        ],
      }],
    },
  ],
  spend: [
    {
      gwan: "사무비",
      hangs: [
        {
          hang: "업무추진비",
          moks: [
            { title: "기관운영비", ids: ["guest", "staffDine", "gift", "bonus", "condolence", "visit", "consult"] },
            { title: "직책보조비", ids: ["positionPay"] },
            { title: "회의비", ids: ["meeting"] },
          ],
        },
        {
          hang: "운영비",
          moks: [
            { title: "여비", ids: ["travel"] },
            { title: "수용비 및 수수료", ids: ["stationery", "supply", "agency", "mgmt", "etcAccept"] },
            { title: "공공요금 및 각종 세금공과금", ids: ["phone", "elec", "water", "liability", "carIns", "tax", "etcTax"] },
            { title: "차량비", ids: ["fuel", "carRepair"] },
            { title: "임차료", ids: ["rent"] },
            { title: "기타운영비", ids: ["rentCar", "reward"] },
          ],
        },
      ],
    },
    {
      gwan: "시설비",
      hangs: [{
        hang: "시설비",
        moks: [
          { title: "시설비", ids: ["fixOffice", "fixBuilding"] },
          { title: "자산취득비", ids: ["installment", "asset"] },
          { title: "시설장비유지비", ids: ["equip"] },
        ],
      }],
    },
    {
      gwan: "사업비",
      hangs: [
        {
          hang: "운영비",
          moks: [
            { title: "생계비", ids: ["living"] },
            { title: "생계비(보조금)", ids: ["livingSub"] },
            { title: "수용기관경비", ids: ["careGoods"] },
            { title: "의료비", ids: ["medical"] },
          ],
        },
        { hang: "사업비", moks: [{ title: "프로그램 사업비", ids: ["program", "programMat"] }] },
      ],
    },
    {
      gwan: "전출금",
      hangs: [{ hang: "전출금", moks: [{ title: "기타전출금", ids: ["transferOut"] }] }],
    },
    {
      gwan: "상환금",
      hangs: [{
        hang: "부채상환금",
        moks: [
          { title: "원금상환금", ids: ["principal"] },
          { title: "이자지불금", ids: ["interestOut"] },
        ],
      }],
    },
    {
      gwan: "잡지출",
      hangs: [{ hang: "잡지출", moks: [{ title: "잡지출", ids: ["refundOut", "miscOut"] }] }],
    },
    {
      gwan: "예비비 및 기타",
      hangs: [{ hang: "예비비 및 기타", moks: [{ title: "반환금", ids: ["giveBack"] }] }],
    },
    {
      gwan: "적립금 및 준비금",
      hangs: [{
        hang: "운영충당적립금 및 환경개선부담금",
        moks: [
          { title: "운영충당적립금", ids: ["reserveSave"] },
          { title: "시설환경 개선준비금", ids: ["improveSave"] },
        ],
      }],
    },
    {
      gwan: "적립금 및 준비금 지출(특별회계)",
      hangs: [{
        hang: "운영충당적립금 및 환경개선부담금 지출",
        moks: [
          { title: "운영충당적립금 지출", ids: ["reserveSpend"] },
          { title: "시설환경개선 준비금 지출", ids: ["improveSpend"] },
        ],
      }],
    },
  ],
};

function fieldsOf(ids: string[], family: BizFamily): MoneyField[] {
  return ids
    .map((id) => FIELD_BY_ID.get(id))
    .filter((field): field is MoneyField => !!field && field.families.includes(family));
}

export function moneyGroups(kind: "income" | "spend", family: BizFamily): MoneySheetGwan[] {
  return SHEET_GWAN[kind]
    .map((gwan) => ({
      gwan: gwan.gwan,
      hangs: gwan.hangs
        .map((hang) => ({
          hang: hang.hang,
          showHang: hang.hang !== gwan.gwan,
          groups: hang.moks
            .map((mok) => ({ title: mok.title, fields: fieldsOf(mok.ids, family) }))
            .filter((group) => group.fields.length > 0),
        }))
        .filter((hang) => hang.groups.length > 0),
    }))
    .filter((gwan) => gwan.hangs.length > 0);
}

export function parseWon(value: string | number | undefined | null): number {
  const n = Number(String(value ?? "").replace(/[^0-9]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export function formatWonInput(raw: string): string {
  const digits = String(raw || "").replace(/[^0-9]/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString();
}

export function lineAmount(line: Pick<WizardLine, "unitPrice" | "count" | "months" | "settledAmount">): number {
  if (line.settledAmount != null) return line.settledAmount;
  return line.unitPrice * line.count * line.months;
}

export function formatWon(n: number): string {
  return Math.round(n).toLocaleString();
}

function pushLine(lines: WizardLine[], line: WizardLine, allowZeroUnit = false) {
  if (line.count <= 0 || line.months <= 0) return;
  if (line.unitPrice < 0) return;
  if (!allowZeroUnit && line.unitPrice <= 0) return;
  lines.push(line);
}

/** 질의서의 3~5등급은 수가표의 3등급 구간으로 계산합니다. */
export function feeGradeKey(label: string): string {
  const text = String(label || "").replace(" (3~5등급)", "").trim();
  if (text === "3~5등급" || text === "3등급") return "3등급";
  return text;
}

export function resolvedGradeRank(family: BizFamily, grade: GradeAnswer, dayHours: string): string {
  if (family === "day") {
    const hours = grade.hours || dayHours;
    const pure = feeGradeKey(grade.label);
    return hours ? `(${hours})${pure}` : "";
  }
  if (family === "facility") return feeGradeKey(grade.label);
  return grade.rank;
}

function moneyToLine(field: MoneyField, bag: { a: string; b: string } | undefined): WizardLine | null {
  const a = parseWon(bag?.a);
  const b = parseWon(bag?.b);
  if (a <= 0) return null;
  if (field.mode === "annual") {
    return baseMoney(field, a, 1, 1);
  }
  if (field.mode === "monthly") {
    return baseMoney(field, a, 1, 12);
  }
  if (field.mode === "span") {
    return baseMoney(field, a, 1, b > 0 ? b : 12);
  }
  if (field.mode === "times") {
    if (b <= 0) return null;
    return baseMoney(field, a, b, 1);
  }
  if (b <= 0) return null;
  return baseMoney(field, a, b, 12);
}

/** stepu 산출기초가 세입을 저장할 때 세출로 같이 만드는 짝. 의료비(116→315)는 stepu에서 꺼져 있어 넣지 않음 */
const INCOME_MIRRORS: { key: string; fromId: string; manualId: string; scode: string; description: string; label: string }[] = [
  { key: "mirror:meal", fromId: "mealIn", manualId: "living", scode: "311", description: "생계비", label: "생계비 (식재료비 수입과 같음)" },
  { key: "mirror:city", fromId: "cityAid", manualId: "livingSub", scode: "312", description: "생계비(보조금)", label: "생계비(보조금) (시군구보조금과 같음)" },
  { key: "mirror:livelihood", fromId: "livelihoodIn", manualId: "livingSub", scode: "312", description: "생계비(보조금)", label: "생계비(보조금) (기초수급자 생계비보조금과 같음)" },
];

export interface WizardIncomeMirror {
  key: string;
  label: string;
  amount: number;
  /** 같은 세출을 이미 직접 적었으면 기본은 빼 둠 */
  defaultOff: boolean;
  scode: string;
  description: string;
  unitPrice: number;
  count: number;
  months: number;
}

/** 식재료비·시군구보조금을 생계비로 복사할 후보. 금액이 있는 것만 */
export function listIncomeMirrors(family: BizFamily, answers: WizardAnswers): WizardIncomeMirror[] {
  const mirrors: WizardIncomeMirror[] = [];
  INCOME_MIRRORS.forEach((spec) => {
    const field = MONEY_FIELDS.find((item) => item.id === spec.fromId);
    if (!field || !field.families.includes(family)) return;
    const line = moneyToLine(field, answers.money[spec.fromId]);
    if (!line) return;
    const manual = MONEY_FIELDS.find((item) => item.id === spec.manualId);
    const manualLine = manual && manual.families.includes(family) ? moneyToLine(manual, answers.money[spec.manualId]) : null;
    mirrors.push({
      key: spec.key,
      label: spec.label,
      amount: lineAmount(line),
      defaultOff: Boolean(manualLine),
      scode: spec.scode,
      description: spec.description,
      unitPrice: line.unitPrice,
      count: line.count,
      months: line.months,
    });
  });
  return mirrors;
}

function baseMoney(field: MoneyField, unitPrice: number, count: number, months: number): WizardLine {
  return {
    trcode: field.trcode,
    scode: field.scode,
    description: field.label,
    reviewLabel: field.label,
    unitPrice,
    count,
    months,
    funding: field.funding,
  };
}

export function dayHourOptions(list: WizardSuga[] | null | undefined): string[] {
  const hours: string[] = [];
  (list || []).forEach((row) => {
    const matched = String(row.suga_rank || "").match(/^\((.+?)\)/);
    if (matched && !hours.includes(matched[1])) hours.push(matched[1]);
  });
  return hours;
}

function defaultRank(family: BizFamily, list: WizardSuga[], gradeLabel: string, dayHours: string): string {
  if (!list.length) return "";
  const key = feeGradeKey(gradeLabel);
  if (family === "day") {
    const exact = `(${dayHours})${key}`;
    if (list.some((row) => row.suga_rank === exact)) return exact;
    return list.find((row) => row.suga_rank.includes(key))?.suga_rank || "";
  }
  if (family === "facility") {
    return list.find((row) => row.suga_rank === key)?.suga_rank || "";
  }
  if (family === "bath") {
    return (
      list.find((row) => /차량\s*내/.test(row.suga_rank))?.suga_rank ||
      list.find((row) => row.suga_rank.includes("가정"))?.suga_rank ||
      list[0].suga_rank
    );
  }
  return list.find((row) => row.suga_rank === "180분")?.suga_rank || list[0].suga_rank;
}

export function preferredDayHours(list: WizardSuga[] | null | undefined): string {
  const hours = dayHourOptions(list);
  return hours.find((hour) => hour.includes("8")) || hours[0] || "";
}

export function gradeLabelOptions(bizGubun: string): string[] {
  const family = budgetWizardFamily(bizGubun);
  if (family === "day") return ["1등급", "2등급", "3~5등급", "3등급", "4등급", "5등급", "인지지원등급"];
  if (family === "facility") return ["1등급", "2등급", "3~5등급"];
  return ["1등급", "2등급", "3~5등급"];
}

function oneGrade(id: string, label: string, rank: string, times: string, hours: string): GradeAnswer {
  return { id, label, count: "", basicCount: "", rank, times, hours };
}

export function createGradeAnswers(bizGubun: string, list: WizardSuga[] | null | undefined): GradeAnswer[] {
  const rows = list || [];
  const family = budgetWizardFamily(bizGubun);
  const times = defaultSugaTimesByBizGubun(bizGubun);
  const hours = preferredDayHours(rows);
  const labels = ["1등급", "2등급", "3~5등급"];
  return labels.map((label, index) =>
    oneGrade(`g${index}`, label, defaultRank(family, rows, label, hours), times, family === "day" ? hours : "")
  );
}

export function extraGrade(
  bizGubun: string,
  list: WizardSuga[] | null | undefined,
  dayHours: string,
  index: number
): GradeAnswer {
  const rows = list || [];
  const family = budgetWizardFamily(bizGubun);
  const times = defaultSugaTimesByBizGubun(bizGubun);
  const hours = dayHours || preferredDayHours(rows);
  if (family === "day") {
    const labels = ["1등급", "2등급", "3~5등급", ...DAY_GRADES];
    const label = labels[index] || "1등급";
    return oneGrade(`g${Date.now()}`, label, defaultRank(family, rows, label, hours), times, hours);
  }
  if (family === "facility") {
    const labels = ["1등급", "2등급", "3~5등급"];
    const label = labels[index] || "1등급";
    return oneGrade(`g${Date.now()}`, label, defaultRank(family, rows, label, hours), times, "");
  }
  return oneGrade(`g${Date.now()}`, "3~5등급", defaultRank(family, rows, "3~5등급", hours), times, "");
}

export function createWizardAnswers(bizGubun: string, list: WizardSuga[]): WizardAnswers {
  const money: WizardAnswers["money"] = {};
  MONEY_FIELDS.forEach((field) => {
    money[field.id] = { a: "", b: field.mode === "span" ? "12" : "" };
  });
  return {
    dayHours: preferredDayHours(list),
    grades: createGradeAnswers(bizGubun, list),
    staff: [
      {
        id: "s1",
        role: "요양보호사",
        kind: "direct",
        isCeo: false,
        count: "",
        monthly: "",
      },
    ],
    treatmentCount: "",
    treatmentSubsidy: "",
    treatmentCopay: "",
    money,
  };
}

/** 예산 표에서 마법사로 되돌릴 때 쓰는 한 줄. */
export interface WizardSheetRow {
  trcode: string;
  scode: string;
  description: string;
  unitPrice: number;
  count: number;
  months: number;
  sugaRank?: string;
  sugaTimes?: string;
  isSalary?: boolean;
  isLinked?: boolean;
}

function digitsText(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(Math.round(n));
}

function wonText(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  return formatWonInput(String(Math.round(n)));
}

function parseSugaMemo(description: string): { rank: string; times: string; basic: boolean } {
  const basic = /기초/.test(description);
  const matched = description.match(/^(.+?)\s*[Xx×]\s*(\d+)(?:\s*기초)?\s*$/);
  if (matched) {
    return { rank: matched[1].replace(/수가/g, "").trim(), times: matched[2], basic };
  }
  return { rank: description.replace(/\s*기초\s*$/, "").trim(), times: "", basic };
}

interface RevenueSlot {
  rank: string;
  times: string;
  basic: boolean;
  people: number;
}

function revenueSlots(rows: WizardSheetRow[], scode: string): RevenueSlot[] {
  const slots: RevenueSlot[] = [];
  rows.forEach((row) => {
    if (row.trcode !== "IN" || row.scode !== scode || row.count <= 0) return;
    const parsed = parseSugaMemo(row.description || "");
    const rank = (row.sugaRank || parsed.rank).trim();
    const times = (row.sugaTimes || parsed.times).trim();
    if (!rank && !parsed.basic) return;
    slots.push({ rank, times, basic: parsed.basic, people: row.count });
  });
  return slots;
}

function collectRevenue(rows: WizardSheetRow[]): RevenueSlot[] {
  const copay = revenueSlots(rows, "112");
  return copay.length ? copay : revenueSlots(rows, "611");
}

function careRankParts(rank: string): { label: string; pure: string } | null {
  const matched = rank.match(/^(1등급|2등급|3~5등급|3등급|4등급|5등급)\s+(.+)$/);
  if (!matched) return null;
  return { label: matched[1] === "3등급" ? "3~5등급" : matched[1], pure: matched[2] };
}

function dayRankParts(rank: string): { hours: string; label: string } {
  const matched = rank.match(/^\((.+?)\)\s*(.+)$/);
  if (matched) {
    return { hours: matched[1], label: matched[2] === "3등급" ? "3~5등급" : matched[2] };
  }
  return { hours: "", label: rank === "3등급" ? "3~5등급" : rank };
}

function applySheetGrades(
  answers: WizardAnswers,
  family: BizFamily,
  bizGubun: string,
  list: WizardSuga[],
  rows: WizardSheetRow[]
) {
  const slots = collectRevenue(rows);
  if (!slots.length) return;
  if (family === "day" || family === "facility") {
    slots.forEach((slot) => {
      const parts = dayRankParts(slot.rank);
      let grade = answers.grades.find((row) => {
        if (feeGradeKey(row.label) !== feeGradeKey(parts.label)) return false;
        return slot.basic ? !row.basicCount : !row.count;
      });
      if (!grade) {
        grade = extraGrade(bizGubun, list, parts.hours || answers.dayHours, answers.grades.length);
        grade.id = `g-sheet-${answers.grades.length}`;
        grade.label = parts.label || grade.label;
        answers.grades.push(grade);
      }
      if (parts.hours) grade.hours = parts.hours;
      if (slot.rank) grade.rank = slot.rank;
      if (slot.times) grade.times = slot.times;
      if (slot.basic) grade.basicCount = digitsText(slot.people);
      else grade.count = digitsText(slot.people);
    });
    const hours = answers.grades.find((row) => row.hours)?.hours;
    if (hours) answers.dayHours = hours;
    return;
  }
  let cursor = 0;
  slots.filter((slot) => !slot.basic).forEach((slot) => {
    const named = careRankParts(slot.rank);
    const pure = named?.pure || slot.rank;
    let grade = named ? answers.grades.find((row) => row.label === named.label) : answers.grades[cursor];
    if (!grade) {
      grade = extraGrade(bizGubun, list, answers.dayHours, answers.grades.length);
      grade.id = `g-sheet-${answers.grades.length}`;
      if (named) grade.label = named.label;
      answers.grades.push(grade);
    }
    if (!named) cursor += 1;
    if (pure) grade.rank = pure;
    if (slot.times) grade.times = slot.times;
    grade.count = digitsText(slot.people);
  });
  slots.filter((slot) => slot.basic).forEach((slot) => {
    const named = careRankParts(slot.rank);
    const pure = named?.pure || slot.rank;
    let grade = answers.grades.find((row) => row.rank === pure) || answers.grades.find((row) => !row.basicCount);
    if (!grade) {
      grade = extraGrade(bizGubun, list, answers.dayHours, answers.grades.length);
      grade.id = `g-sheet-${answers.grades.length}`;
      answers.grades.push(grade);
    }
    if (pure) grade.rank = pure;
    if (slot.times) grade.times = slot.times;
    grade.basicCount = digitsText(slot.people);
  });
}

function isSalarySheetRow(row: WizardSheetRow): boolean {
  if (row.trcode !== "OUT" || (row.scode !== "1111" && row.scode !== "1112")) return false;
  if (row.isLinked || row.unitPrice <= 0 || row.count <= 0) return false;
  if (/사회보험|퇴직/.test(row.description || "")) return false;
  return Boolean(row.isSalary) || /급여/.test(row.description || "");
}

function applySheetStaff(answers: WizardAnswers, bizGubun: string, rows: WizardSheetRow[]) {
  const found: StaffAnswer[] = [];
  rows.forEach((row) => {
    if (!isSalarySheetRow(row)) return;
    const text = (row.description || "").replace(/\s*급여\s*$/, "").trim();
    const isCeo = text.includes("(대표자)");
    const role = text.replace("(대표자)", "").trim() || "요양보호사";
    const kind: StaffAnswer["kind"] = row.scode === "1112" ? "indirect" : "direct";
    found.push({
      id: `s-sheet-${found.length + 1}`,
      role,
      kind,
      isCeo,
      count: digitsText(row.count),
      monthly: wonText(row.unitPrice),
    });
  });
  if (found.length) answers.staff.splice(0, answers.staff.length, ...found);
}

function applySheetTreatment(answers: WizardAnswers, rows: WizardSheetRow[]) {
  rows.forEach((row) => {
    if (row.trcode !== "OUT" || row.scode !== "1161" || row.unitPrice <= 0) return;
    const description = row.description || "";
    if (description.includes("보조금")) {
      answers.treatmentSubsidy = wonText(row.unitPrice);
      if (row.count > 0) answers.treatmentCount = digitsText(row.count);
    } else if (description.includes("본인부담")) {
      answers.treatmentCopay = wonText(row.unitPrice);
      if (row.count > 0 && !answers.treatmentCount) answers.treatmentCount = digitsText(row.count);
    }
  });
}

const SHEET_SKIP_OUT = new Set(["1111", "1112", "1141", "1142", "1151", "1152", "1161"]);

function applySheetMoney(answers: WizardAnswers, family: BizFamily, rows: WizardSheetRow[]) {
  const fields = MONEY_FIELDS.filter((field) => field.families.includes(family));
  rows.forEach((row) => {
    if (row.unitPrice <= 0 || row.count <= 0 || row.months <= 0) return;
    const description = (row.description || "").trim();
    if (row.trcode === "IN" && (row.scode === "112" || row.scode === "611")) return;
    if (row.trcode === "OUT" && SHEET_SKIP_OUT.has(row.scode)) return;
    const same = fields.filter((field) => field.trcode === row.trcode && field.scode === row.scode);
    const field = same.find((item) => item.label === description);
    if (!field) return;
    const bag = answers.money[field.id];
    if (!bag) return;
    if (field.mode === "annual") {
      bag.a = wonText(parseWon(bag.a) + row.unitPrice * row.count * row.months);
    } else if (field.mode === "monthly") {
      bag.a = wonText(parseWon(bag.a) + row.unitPrice * row.count);
    } else if (field.mode === "span") {
      bag.a = wonText(row.unitPrice * row.count);
      bag.b = digitsText(row.months) || bag.b;
    } else {
      bag.a = wonText(row.unitPrice);
      bag.b = digitsText(parseWon(bag.b) + row.count);
    }
  });
}

/** 지금 예산 표의 단가·인원·횟수·적요를 마법사 입력으로 되돌립니다. */
export function hydrateWizardAnswers(
  bizGubun: string,
  list: WizardSuga[],
  rows: WizardSheetRow[]
): WizardAnswers {
  const answers = createWizardAnswers(bizGubun, list);
  const family = budgetWizardFamily(bizGubun);
  applySheetGrades(answers, family, bizGubun, list, rows);
  applySheetStaff(answers, bizGubun, rows);
  applySheetTreatment(answers, rows);
  applySheetMoney(answers, family, rows);
  return answers;
}

const WIZARD_DRAFT_PREFIX = "caion.budgetWizard.draft.v1";

export interface WizardDraft {
  savedAt: string;
  stepIndex: number;
  answers: WizardAnswers;
}

export function readWizardDraft(key: string): WizardDraft | null {
  if (!key || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${WIZARD_DRAFT_PREFIX}:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardDraft;
    if (!parsed || !parsed.answers || !Array.isArray(parsed.answers.grades)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeWizardDraft(key: string, draft: WizardDraft): void {
  if (!key || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(`${WIZARD_DRAFT_PREFIX}:${key}`, JSON.stringify(draft));
  } catch {
    /* 저장 공간이 없으면 화면의 값은 유지하고 넘어갑니다. */
  }
}

export function clearWizardDraft(key: string): void {
  if (!key || typeof localStorage === "undefined") return;
  localStorage.removeItem(`${WIZARD_DRAFT_PREFIX}:${key}`);
}

export function insurancePerPerson(monthly: number, isCeo: boolean, tax: WizardTax | null | undefined): number {
  if (!monthly || !tax || isCeo) return 0;
  const national = Math.round(monthly * (tax.tr_yeon || 0));
  const health = Math.round(monthly * (tax.tr_gun || 0));
  const care = Math.round(health * (tax.tr_jang || 0));
  const employment = Math.round(monthly * (tax.tr_go || 0));
  const unemployment = Math.round(monthly * (tax.tr_sil || 0));
  const accident = Math.round(monthly * (tax.tr_san || 0));
  return national + health + care + employment + unemployment + accident;
}

export function retirementPerPerson(monthly: number, isCeo: boolean): number {
  if (!monthly || isCeo) return 0;
  return Math.round(monthly / 12);
}

/** 예산표 getExactAmount 와 동일: 인원 합산 월급에 요율을 곱한 뒤 반올림 */
export function insuranceGridTotal(
  monthly: number,
  count: number,
  months: number,
  isCeo: boolean,
  tax: WizardTax | null | undefined
): number {
  if (!monthly || !count || !months || !tax || isCeo) return 0;
  const payroll = monthly * count;
  const national = Math.round(payroll * (tax.tr_yeon || 0));
  const health = Math.round(payroll * (tax.tr_gun || 0));
  const care = Math.round(health * (tax.tr_jang || 0));
  const employment = Math.round(payroll * (tax.tr_go || 0));
  const unemployment = Math.round(payroll * (tax.tr_sil || 0));
  const accident = Math.round(payroll * (tax.tr_san || 0));
  return (national + health + care + employment + unemployment + accident) * months;
}

/** 예산표 퇴직금: 월급×인원×개월/12 을 한 번만 반올림. 대표자는 0 */
export function retirementGridTotal(monthly: number, count: number, months: number, isCeo: boolean): number {
  if (!monthly || !count || !months || isCeo) return 0;
  return Math.round((monthly * count * months) / 12);
}

export function previewPair(
  suga: WizardSuga | undefined,
  times: number,
  basic: boolean
): { bonin: number; benefit: number } {
  if (!suga || times <= 0) return { bonin: 0, benefit: 0 };
  if (basic) return { bonin: 0, benefit: suga.suga_money * times };
  return {
    bonin: suga.suga_bonin * times,
    benefit: (suga.suga_money - suga.suga_bonin) * times,
  };
}

function findSuga(list: WizardSuga[], rank: string): WizardSuga | undefined {
  return list.find((row) => row.suga_rank === rank);
}

function addRevenuePair(
  lines: WizardLine[],
  args: {
    label: string;
    rank: string;
    times: number;
    people: number;
    basic: boolean;
    suga: WizardSuga;
    rel: string;
  }
) {
  const unit = previewPair(args.suga, args.times, args.basic);
  if (unit.bonin <= 0 && unit.benefit <= 0) return;
  const who = args.basic ? `${args.label} 기초수급자` : args.label;
  const memo = args.basic ? `${args.rank} X ${args.times} 기초` : `${args.rank} X ${args.times}`;
  const common = {
    trcode: "IN" as const,
    count: args.people,
    months: 12,
    sugaRank: args.rank,
    sugaTimes: String(args.times),
    revenueRel: args.rel,
  };
  pushLine(lines, {
    ...common,
    scode: "112",
    description: memo,
    reviewLabel: `${who} 본인부담금`,
    unitPrice: unit.bonin,
  }, true);
  pushLine(lines, {
    ...common,
    scode: "611",
    description: memo,
    reviewLabel: `${who} 장기요양급여`,
    unitPrice: unit.benefit,
  }, true);
}

let relSeq = 0;
function nextRel(prefix: string): string {
  relSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${relSeq}`;
}

export interface LaborStatus {
  base: number;
  labor: number;
  rate: number;
  required: number;
  shortMoney: number;
  ok: boolean;
}

export function laborStatus(lines: WizardLine[], requiredPercent?: number): LaborStatus {
  let base = 0;
  let labor = 0;
  lines.forEach((line) => {
    const amount = lineAmount(line);
    if (line.trcode === "IN" && (line.scode === "112" || line.scode === "611" || line.scode === "613")) base += amount;
    if (line.trcode === "OUT" && (line.scode === "1111" || line.scode === "1141" || line.scode === "1151")) labor += amount;
  });
  const required = requiredPercent && requiredPercent > 0 ? requiredPercent : 60;
  const rate = base > 0 ? (labor / base) * 100 : 0;
  const shortMoney = base > 0 ? Math.max(0, Math.ceil((required / 100) * base - labor)) : 0;
  return { base, labor, rate, required, shortMoney, ok: base > 0 && shortMoney === 0 };
}

export function buildWizardLines(
  bizGubun: string,
  answers: WizardAnswers,
  sugaList: WizardSuga[],
  tax: WizardTax | null | undefined,
  excludeAutoKeys?: readonly string[]
): WizardBuildResult {
  const family = budgetWizardFamily(bizGubun);
  const lines: WizardLine[] = [];
  // "직원id:ins" / "직원id:ret" — 표에 넣기에서 뺀 자동 계산(사회보험·퇴직적립금)
  const excludedAuto = new Set(excludeAutoKeys || []);

  answers.grades.forEach((grade) => {
    const rank = resolvedGradeRank(family, grade, answers.dayHours);
    const suga = findSuga(sugaList, rank);
    const times = parseWon(grade.times);
    const people = parseWon(grade.count);
    const basic = parseWon(grade.basicCount);
    if (!suga || times <= 0) return;
    const shown = family === "care" || family === "bath" ? `${grade.label} ${rank}`.trim() : grade.label;
    if (people > 0) {
      addRevenuePair(lines, {
        label: shown,
        rank,
        times,
        people,
        basic: false,
        suga,
        rel: nextRel("rev"),
      });
    }
    if (basic > 0 && familyUsesBasicSplit(family)) {
      addRevenuePair(lines, {
        label: shown,
        rank,
        times,
        people: basic,
        basic: true,
        suga,
        rel: nextRel("rev"),
      });
    }
  });

  fieldsFor("benefit", family)
    .concat(fieldsFor("subsidy", family), fieldsFor("carry", family))
    .forEach((field) => {
      const line = moneyToLine(field, answers.money[field.id]);
      if (line) lines.push(line);
    });

  answers.staff.forEach((person) => {
    const count = parseWon(person.count);
    const monthly = parseWon(person.monthly);
    if (count <= 0 || monthly <= 0) return;
    const payRel = nextRel("pay");
    const roleName = `${person.role}${person.isCeo ? "(대표자)" : ""}`;
    const salaryScode = person.kind === "indirect" ? "1112" : "1111";
    const insuranceScode = person.kind === "indirect" ? "1152" : "1151";
    const retireScode = person.kind === "indirect" ? "1142" : "1141";
    pushLine(lines, {
      trcode: "OUT",
      scode: salaryScode,
      description: `${roleName} 급여`,
      reviewLabel: `${roleName} 급여`,
      unitPrice: monthly,
      count,
      months: 12,
      payRel,
      isSalary: true,
    });
    const ins = insurancePerPerson(monthly, person.isCeo, tax);
    if (!excludedAuto.has(`${person.id}:ins`)) {
      pushLine(lines, {
        trcode: "OUT",
        scode: insuranceScode,
        description: `${roleName} 사회보험부담금`,
        reviewLabel: `${roleName} 사회보험`,
        unitPrice: ins,
        count,
        months: 12,
        payRel,
        isLinked: true,
        settledAmount: insuranceGridTotal(monthly, count, 12, person.isCeo, tax),
      });
    }
    const retire = retirementPerPerson(monthly, person.isCeo);
    if (!excludedAuto.has(`${person.id}:ret`)) {
      pushLine(lines, {
        trcode: "OUT",
        scode: retireScode,
        description: `${roleName} 퇴직금`,
        reviewLabel: `${roleName} 퇴직적립금`,
        unitPrice: retire,
        count,
        months: 12,
        payRel,
        isLinked: true,
        settledAmount: retirementGridTotal(monthly, count, 12, person.isCeo),
      });
    }
  });

  if (familyUsesTreatment(family)) {
    const count = parseWon(answers.treatmentCount);
    const subsidy = parseWon(answers.treatmentSubsidy);
    const copay = parseWon(answers.treatmentCopay);
    if (count > 0 && subsidy > 0) {
      pushLine(lines, {
        trcode: "OUT",
        scode: "1161",
        description: "처우개선비(보조금)",
        reviewLabel: "처우개선비 (보조금)",
        unitPrice: subsidy,
        count,
        months: 12,
      });
    }
    if (count > 0 && copay > 0) {
      pushLine(lines, {
        trcode: "OUT",
        scode: "1161",
        description: "처우개선비(본인부담)",
        reviewLabel: "처우개선비 (본인부담)",
        unitPrice: copay,
        count,
        months: 12,
      });
    }
  }

  (["operate", "office", "vehicle", "place", "closing"] as const).forEach((stepId) => {
    fieldsFor(stepId, family).forEach((field) => {
      const line = moneyToLine(field, answers.money[field.id]);
      if (line) lines.push(line);
    });
  });

  listIncomeMirrors(family, answers).forEach((mirror) => {
    if (excludedAuto.has(mirror.key)) return;
    pushLine(lines, {
      trcode: "OUT",
      scode: mirror.scode,
      description: mirror.description,
      reviewLabel: mirror.label,
      unitPrice: mirror.unitPrice,
      count: mirror.count,
      months: mirror.months,
    });
  });

  let income = 0;
  let expense = 0;
  lines.forEach((line) => {
    const amount = lineAmount(line);
    if (line.trcode === "IN") income += amount;
    else expense += amount;
  });

  return { lines, income, expense, plug: 0, shortfall: 0 };
}

export function modeAmountLabel(mode: MoneyMode): { a: string; b: string } {
  if (mode === "times") return { a: "1회 금액", b: "횟수" };
  if (mode === "people") return { a: "1인 월 금액", b: "인원" };
  if (mode === "span") return { a: "월 금액", b: "12" };
  if (mode === "monthly") return { a: "한 달 금액", b: "" };
  return { a: "연간 금액", b: "" };
}
